import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import { getFaqKey } from '../utils/keys';

const DomesticWorkersVisaPage = () => {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: t('dwFaq1Q'),
      answer: t('dwFaq1A')
    },
    {
      question: t('dwFaq2Q'),
      answer: t('dwFaq2A')
    },
    {
      question: t('dwFaq3Q'),
      answer: t('dwFaq3A')
    },
    {
      question: t('dwFaq4Q'),
      answer: t('dwFaq4A')
    },
    {
      question: t('dwFaq5Q'),
      answer: t('dwFaq5A')
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section with Background Image */}
      <section 
        className="relative h-[500px] bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1652784549134-bae822a7c4a3?w=1920&q=80)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            <div className="inline-block bg-yellow-400 text-black px-4 py-2 rounded font-bold mb-4">
              {t('dwHeroBadge')}
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('dwHeroTitle')}
            </h1>
            <p className="text-xl text-white/90 mb-8">
              {t('dwHeroDesc')}
            </p>
            <Link
              to="/apply?destination=Europe&type=Domestic Worker"
              className="inline-block bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all"
            >
              {t('getFreeConsultation')}
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-16">
          <p className="text-gray-700 text-lg leading-relaxed">
            {t('dwIntro')}
          </p>
        </div>

        {/* Features Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('dwFeaturesTitle')}</h2>
          <p className="text-gray-700 text-lg mb-6">
            {t('dwFeaturesIntro')}
          </p>
          
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span><strong>{t('dwFeature1Title')}</strong> {t('dwFeature1Desc')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span><strong>{t('dwFeature2Title')}</strong> {t('dwFeature2Desc')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span><strong>{t('dwFeature3Title')}</strong> {t('dwFeature3Desc')}</span>
            </li>
          </ul>
        </div>

        {/* Why Rejected Section */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('dwRejectedTitle')}</h3>
          <p className="text-gray-700 text-lg mb-6">
            {t('dwRejectedP1')}
          </p>
          <p className="text-gray-700 text-lg">
            {t('dwRejectedP2')}
          </p>
        </div>

        {/* Documents Section */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('dwDocsTitle')}</h3>
          <p className="text-gray-700 text-lg mb-6">
            {t('dwDocsIntro')}
          </p>
          
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span><strong>{t('dwDoc1Title')}</strong> {t('dwDoc1Desc')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span><strong>{t('dwDoc2Title')}</strong> {t('dwDoc2Desc')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span><strong>{t('dwDoc3Title')}</strong> {t('dwDoc3Desc')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span><strong>{t('dwDoc4Title')}</strong> {t('dwDoc4Desc')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span><strong>{t('dwDoc5Title')}</strong> {t('dwDoc5Desc')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span><strong>{t('dwDoc6Title')}</strong> {t('dwDoc6Desc')}</span>
            </li>
          </ul>
        </div>

        {/* Processing Times */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('dwTimelinesTitle')}</h3>
          <p className="text-gray-700 text-lg mb-6">
            {t('dwTimelinesIntro')}
          </p>
          
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span><strong>{t('dwTimeline1')}</strong> – {t('dwTimeline1Desc')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span><strong>{t('dwTimeline2')}</strong> – {t('dwTimeline2Desc')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span><strong>{t('dwTimeline3')}</strong> – {t('dwTimeline3Desc')}</span>
            </li>
          </ul>
        </div>

        {/* Why Choose Us */}
        <div className="mb-16 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 lg:p-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('dwWhyChooseTitle')}</h3>
          <p className="text-gray-700 text-lg mb-6">
            {t('dwWhyChooseIntro')}
          </p>
          
          <p className="text-gray-700 text-lg mb-4"><strong>{t('dwWhyChooseSubtitle')}</strong></p>
          
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <Check className="w-6 h-6 text-logo-cyan mr-3 flex-shrink-0 mt-0.5" />
              <span><strong>{t('dwWhyChoose1Title')}</strong> {t('dwWhyChoose1Desc')}</span>
            </li>
            <li className="flex items-start">
              <Check className="w-6 h-6 text-logo-cyan mr-3 flex-shrink-0 mt-0.5" />
              <span><strong>{t('dwWhyChoose2Title')}</strong> {t('dwWhyChoose2Desc')}</span>
            </li>
            <li className="flex items-start">
              <Check className="w-6 h-6 text-logo-cyan mr-3 flex-shrink-0 mt-0.5" />
              <span><strong>{t('dwWhyChoose3Title')}</strong> {t('dwWhyChoose3Desc')}</span>
            </li>
            <li className="flex items-start">
              <Check className="w-6 h-6 text-logo-cyan mr-3 flex-shrink-0 mt-0.5" />
              <span><strong>{t('dwWhyChoose4Title')}</strong> {t('dwWhyChoose4Desc')}</span>
            </li>
            <li className="flex items-start">
              <Check className="w-6 h-6 text-logo-cyan mr-3 flex-shrink-0 mt-0.5" />
              <span><strong>{t('dwWhyChoose5Title')}</strong> {t('dwWhyChoose5Desc')}</span>
            </li>
            <li className="flex items-start">
              <Check className="w-6 h-6 text-logo-cyan mr-3 flex-shrink-0 mt-0.5" />
              <span><strong>{t('dwWhyChoose6Title')}</strong> {t('dwWhyChoose6Desc')}</span>
            </li>
          </ul>
        </div>

        {/* FAQ Section */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">{t('dwFaqTitle')}</h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={getFaqKey(faq.question, index)} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-4 bg-gray-50">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Packages Table */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t('dwPackagesTitle')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-4 text-left font-bold text-gray-900">{t('tableFeatures')}</th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900">
                    <div>{t('dwTable_standard')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('dwTable_duration2to4weeks')}</div>
                  </th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900 bg-blue-50">
                    <div>{t('dwTable_premium')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('dwTable_duration1week')}</div>
                  </th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900">
                    <div>{t('dwTable_vip')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('dwTable_duration2to3days')}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 p-3 text-gray-700">{t('dwFeature_appointmentScheduling')}</td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 text-gray-700">{t('dwFeature_dedicatedCaseManager')}</td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3 text-gray-700">{t('dwFeature_embassyGradeDoc')}</td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 text-gray-700">{t('dwFeature_travelInsurance')}</td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3 text-gray-700">{t('dwFeature_nocLetter')}</td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 text-gray-700">{t('dwFeature_expressAppointment')}</td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3 text-gray-700">{t('dwFeature_seniorManager')}</td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 text-gray-700">{t('dwFeature_aiReview')}</td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3 text-gray-700">{t('dwFeature_homeDelivery')}</td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 text-gray-700">{t('dwFeature_fastTrack')}</td>
                  <td className="border border-gray-300 p-3 text-center"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center bg-blue-50"><X className="w-5 h-5 text-gray-400 mx-auto" /></td>
                  <td className="border border-gray-300 p-3 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-4"></td>
                  <td className="border border-gray-300 p-4 text-center">
                    <Link to="/apply" className="inline-block bg-gray-200 hover:bg-gray-300 text-gray-900 px-6 py-2 rounded font-semibold transition-colors">
                      {t('getInTouch')}
                    </Link>
                  </td>
                  <td className="border border-gray-300 p-4 text-center bg-blue-50">
                    <Link to="/apply" className="inline-block bg-gradient-to-r from-logo-blue to-logo-cyan hover:shadow-lg text-white px-6 py-2 rounded font-semibold transition-all">
                      {t('getInTouch')}
                    </Link>
                  </td>
                  <td className="border border-gray-300 p-4 text-center">
                    <Link to="/apply" className="inline-block bg-gray-200 hover:bg-gray-300 text-gray-900 px-6 py-2 rounded font-semibold transition-colors">
                      {t('getInTouch')}
                    </Link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
};

export default DomesticWorkersVisaPage;
